/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useEffect, useRef, useState } from "react";
import debounce from "lodash.debounce";
import { When } from "react-if";
import {
  Box,
  Button,
  FlexContainer,
  Input,
  Sidebar,
  theme,
} from "@nordcloud/gnui";
import { isNotNil } from "~/tools";
import { LoaderWrap } from "../Utils";
import { SidebarItemList } from "./SidebarItemList";
import { MultiSelectSubmitContainer } from "./styles";
import {
  Item,
  SidebarSelectorProps,
  SidebarWithSelectAll,
  UISidebarProps,
} from "./types";
import { getMultiSelectLabel } from "./utils";

type Props = SidebarSelectorProps & SidebarWithSelectAll & UISidebarProps;

const DEBOUNCE_TIMEOUT_MS = 300;

export function SidebarSelector({
  options,
  hasIcon,
  name,
  pluralName,
  hasSubtitle,
  isOpen,
  onClick,
  onSubmit,
  onMultiSubmit,
  selectedOptions,
  showLoader = false,
  footer,
  searchFilter,
  setSearchFilter = undefined,
  removedSelections,
  selectButtonLabel,
  onClear,
  onSelectAll,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [filteredOptions, setFilteredOptions] = useState<Item[]>();
  const [search, setSearch] = useState<string>(searchFilter ?? "");

  useEffect(() => {
    if (setSearchFilter === undefined) {
      filterBySearch();
    } else {
      debouncedSearch();
    }
  }, [search]);

  const debouncedSearch = debounce(() => {
    if (setSearchFilter !== undefined) {
      setSearchFilter(search);
    }
  }, DEBOUNCE_TIMEOUT_MS);

  useEffect(() => {
    setFilteredOptions(options);

    const inputRefCurrent = inputRef?.current;
    if (isOpen && inputRefCurrent) {
      inputRefCurrent.focus();
    }
  }, [options, isOpen]);

  const filterBySearch = () => {
    setFilteredOptions(
      options.filter(
        ({ label, value }) =>
          label?.toLowerCase().includes(search) ||
          value?.toLowerCase().includes(search)
      )
    );
  };

  const selectedCount = (selectedOptions ?? []).length;
  const deselectedCount = (removedSelections ?? []).length;

  const handleClick = () => {
    setSearch("");
    onClick();
  };

  const showSelectAll = isNotNil(onSelectAll) && isNotNil(onClear);

  return (
    <Sidebar
      title={`Select ${name}`}
      isOpen={isOpen}
      headerStyles={{ zIndex: 2 }}
      footer={
        <div css={{ width: "100%", flexDirection: "column" }}>
          <When condition={footer !== undefined}>{footer}</When>
          <When condition={selectedCount > 0 || deselectedCount > 0}>
            <MultiSelectSubmitContainer>
              <Button data-testid="sidebar-submit" onClick={onMultiSubmit}>
                {selectButtonLabel ??
                  getMultiSelectLabel(selectedCount, name ?? "", pluralName)}
              </Button>
            </MultiSelectSubmitContainer>
          </When>
        </div>
      }
      onClick={handleClick}
    >
      <LoaderWrap inContent loading={showLoader}>
        <Box boxStyle="lightGrey" mb={theme.spacing.spacing04}>
          <FlexContainer>
            <div css={{ flexGrow: 1 }}>
              <Input
                ref={inputRef}
                small
                type="text"
                placeholder="Search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>
          </FlexContainer>
        </Box>
        <When condition={showSelectAll}>
          <FlexContainer justifyContent="space-between">
            <Button size="sm" severity="medium" onClick={onSelectAll}>
              Select All
            </Button>
            <Button size="sm" severity="medium" onClick={onClear}>
              Clear Selection
            </Button>
          </FlexContainer>
        </When>
        <SidebarItemList
          items={[...(filteredOptions ?? [])]}
          hasSubtitle={hasSubtitle}
          hasIcon={hasIcon}
          name={name}
          selectedOptions={selectedOptions}
          onSubmit={onSubmit}
        />
      </LoaderWrap>
    </Sidebar>
  );
}
